import React, { useState } from "react";
import { Link } from "react-router-dom";
import { api, toFieldErrors, isValidEmail } from "@/lib/api";
import Navbar from "@/components/Navbar";
import { Crosshair } from "@/components/Crosshair";
import { Briefcase, CheckCircle2, ShieldCheck, TrendingUp, Star } from "lucide-react";

const US_STATES = ["AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "DC", "FL", "GA", "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ", "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"];

const DESC_MAX = 750;

// Friendly per-field messages — shared by client checks and server (422) mapping
// so each error renders inline under its own field regardless of which layer catches it.
const FIELD_MESSAGES = {
  business_name: "Enter a business or company name (2–100 characters).",
  business_description: `Keep the description under ${DESC_MAX} characters.`,
  tax_id: "Tax ID / EIN can be up to 20 characters.",
  website: "Enter a valid website (up to 200 characters).",
  first_name: "Enter the contact's first name.",
  last_name: "Enter the contact's last name.",
  email: "Enter a valid email address.",
  phone: "Enter a contact phone number (5–20 characters).",
  street_address: "Enter the business street address.",
  city: "Enter the city.",
  state: "Select a state.",
  zip_code: "Enter a valid ZIP code (3–10 characters).",
  agree_terms: "You must agree to the Dealer Terms.",
};

const EMPTY = {
  business_name: "", business_description: "", tax_id: "", website: "",
  first_name: "", last_name: "", email: "", phone: "",
  street_address: "", city: "", state: "", zip_code: "", agree_terms: false,
};

const inputCls = (err) =>
  `mt-1 bg-input border ${err ? "border-blaze" : "border-sniper focus:border-olive"} outline-none px-3 py-2.5 text-foreground transition-colors`;

const labelCls = "font-mono text-xs uppercase tracking-wide text-secondary-foreground";

function FieldWrap({ label, htmlFor, error, className = "", children, hint }) {
  return (
    <div className={className}>
      <div className="flex items-baseline justify-between gap-2">
        <label htmlFor={htmlFor} className={labelCls}>{label}</label>
        {hint}
      </div>
      {children}
      {error && <p role="alert" className="mt-1 text-xs font-mono text-blaze">{error}</p>}
    </div>
  );
}

export default function DealerApply() {
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;
    setForm((f) => ({ ...f, [k]: v }));
    if (errors[k]) setErrors((p) => ({ ...p, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    const t = (k) => form[k].trim();
    if (t("business_name").length < 2 || t("business_name").length > 100) e.business_name = FIELD_MESSAGES.business_name;
    if (form.business_description.length > DESC_MAX) e.business_description = FIELD_MESSAGES.business_description;
    if (form.tax_id.length > 20) e.tax_id = FIELD_MESSAGES.tax_id;
    if (form.website && (form.website.length > 200 || !/\./.test(form.website))) e.website = FIELD_MESSAGES.website;
    if (t("first_name").length < 1 || t("first_name").length > 50) e.first_name = FIELD_MESSAGES.first_name;
    if (t("last_name").length < 1 || t("last_name").length > 50) e.last_name = FIELD_MESSAGES.last_name;
    if (!isValidEmail(t("email"))) e.email = FIELD_MESSAGES.email;
    if (t("phone").length < 5 || t("phone").length > 20) e.phone = FIELD_MESSAGES.phone;
    if (t("street_address").length < 3 || t("street_address").length > 100) e.street_address = FIELD_MESSAGES.street_address;
    if (t("city").length < 2 || t("city").length > 60) e.city = FIELD_MESSAGES.city;
    if (!form.state) e.state = FIELD_MESSAGES.state;
    if (t("zip_code").length < 3 || t("zip_code").length > 10) e.zip_code = FIELD_MESSAGES.zip_code;
    if (!form.agree_terms) e.agree_terms = FIELD_MESSAGES.agree_terms;
    return e;
  };

  const submit = async (ev) => {
    ev.preventDefault();
    const e = validate();
    setErrors(e);
    if (Object.keys(e).length) return;
    setBusy(true);
    try {
      await api.post("/dealer/apply", form);
      setDone(true);
    } catch (err) {
      const { fields, general } = toFieldErrors(err.response?.data?.detail);
      const mapped = {};
      Object.keys(fields).forEach((k) => { mapped[k] = FIELD_MESSAGES[k] || fields[k]; });
      if (Object.keys(mapped).length) setErrors(mapped);
      else setErrors({ form: general || "Something went wrong. Please try again." });
    } finally {
      setBusy(false);
    }
  };

  const descLeft = form.business_description.length;

  return (
    <div className="min-h-screen text-foreground">
      <Navbar />
      <div className="max-w-2xl mx-auto px-4 py-10">
        <div className="flex items-center gap-2">
          <Briefcase className="w-6 h-6 text-teal-300" strokeWidth={1.6} />
          <span className="font-mono text-[11px] uppercase tracking-[0.3em] text-teal-300">Dealer Program</span>
        </div>
        <h1 className="font-heading text-5xl font-black tracking-tighter mt-3">APPLY FOR <span className="text-olive-bright">DEALER STATUS</span></h1>
        <p className="mt-3 text-sm font-body text-secondary-foreground">
          For shops and high-volume sellers. Get a Dealer badge, higher listing limits, and priority placement.
        </p>

        <div className="grid grid-cols-3 gap-3 mt-6">
          {[[Star, "Dealer badge"], [TrendingUp, "Higher limits"], [ShieldCheck, "Priority placement"]].map(([Icon, t], i) => (
            <div key={i} className="border border-sniper bg-surface p-3 text-center">
              <Icon className="w-5 h-5 text-olive-bright mx-auto" strokeWidth={1.5} />
              <p className="text-[11px] font-mono uppercase text-secondary-foreground mt-1.5">{t}</p>
            </div>
          ))}
        </div>

        <Crosshair className="my-7" />

        {done ? (
          <div className="border border-olive/50 bg-olive/10 p-8 text-center" data-testid="dealer-success">
            <CheckCircle2 className="w-12 h-12 text-olive-bright mx-auto" strokeWidth={1.4} />
            <h2 className="font-heading text-3xl uppercase mt-3">Application Received</h2>
            <p className="text-sm font-body text-secondary-foreground mt-2">
              Our team will review your Dealer status and follow up by email. You can keep using OpticSwap as a regular seller in the meantime.
            </p>
            <Link to="/feed" className="inline-block mt-5 bg-olive hover:bg-olive-bright text-white font-heading font-bold uppercase px-6 py-3 transition-colors">Back to Marketplace</Link>
          </div>
        ) : (
          <form onSubmit={submit} noValidate className="space-y-4" data-testid="dealer-form">
            {errors.form && <div role="alert" className="border border-blaze/50 bg-blaze/10 text-blaze text-sm px-3 py-2 font-mono">{errors.form}</div>}

            {/* Business / Company Name — wide */}
            <FieldWrap label="Business / Company Name" htmlFor="d-name" error={errors.business_name}>
              <input id="d-name" data-testid="dealer-business-name" maxLength={100} value={form.business_name} onChange={set("business_name")}
                aria-invalid={!!errors.business_name} className={`${inputCls(errors.business_name)} w-full sm:max-w-md`} />
            </FieldWrap>

            {/* Business Description — full-width textarea + live counter */}
            <FieldWrap
              label="Business Description"
              htmlFor="d-desc"
              error={errors.business_description}
              hint={<span data-testid="dealer-desc-counter" className={`font-mono text-[10px] ${descLeft > DESC_MAX ? "text-blaze" : "text-muted-foreground"}`}>{descLeft}/{DESC_MAX}</span>}
            >
              <textarea id="d-desc" data-testid="dealer-description" maxLength={DESC_MAX} rows={4} value={form.business_description} onChange={set("business_description")}
                placeholder="Tell us about your shop — what you sell, years in business, storefront or online, etc."
                aria-invalid={!!errors.business_description} className={`${inputCls(errors.business_description)} w-full resize-y`} />
            </FieldWrap>

            {/* Tax ID (narrow) + Website (medium) */}
            <div className="flex flex-wrap gap-4">
              <FieldWrap label="Tax ID / EIN" htmlFor="d-tax" error={errors.tax_id}>
                <input id="d-tax" data-testid="dealer-tax-id" maxLength={20} value={form.tax_id} onChange={set("tax_id")}
                  placeholder="XX-XXXXXXX" aria-invalid={!!errors.tax_id} className={`${inputCls(errors.tax_id)} w-full sm:w-40`} />
              </FieldWrap>
              <FieldWrap label="Business Website" htmlFor="d-web" error={errors.website} className="flex-1 min-w-[240px]">
                <input id="d-web" data-testid="dealer-website" maxLength={200} value={form.website} onChange={set("website")}
                  placeholder="https://" aria-invalid={!!errors.website} className={`${inputCls(errors.website)} w-full sm:max-w-sm`} />
              </FieldWrap>
            </div>

            {/* First / Last name — side by side */}
            <div className="grid sm:grid-cols-2 gap-4">
              <FieldWrap label="Contact First Name" htmlFor="d-first" error={errors.first_name}>
                <input id="d-first" data-testid="dealer-first-name" maxLength={50} value={form.first_name} onChange={set("first_name")}
                  aria-invalid={!!errors.first_name} className={`${inputCls(errors.first_name)} w-full`} />
              </FieldWrap>
              <FieldWrap label="Contact Last Name" htmlFor="d-last" error={errors.last_name}>
                <input id="d-last" data-testid="dealer-last-name" maxLength={50} value={form.last_name} onChange={set("last_name")}
                  aria-invalid={!!errors.last_name} className={`${inputCls(errors.last_name)} w-full`} />
              </FieldWrap>
            </div>

            {/* Email (medium-wide) + Phone (narrow) */}
            <div className="flex flex-wrap gap-4">
              <FieldWrap label="Email" htmlFor="d-email" error={errors.email} className="flex-1 min-w-[240px]">
                <input id="d-email" data-testid="dealer-email" type="email" maxLength={254} value={form.email} onChange={set("email")}
                  aria-invalid={!!errors.email} className={`${inputCls(errors.email)} w-full sm:max-w-md`} />
              </FieldWrap>
              <FieldWrap label="Phone" htmlFor="d-phone" error={errors.phone}>
                <input id="d-phone" data-testid="dealer-phone" maxLength={20} value={form.phone} onChange={set("phone")}
                  aria-invalid={!!errors.phone} className={`${inputCls(errors.phone)} w-full sm:w-44`} />
              </FieldWrap>
            </div>

            {/* Street Address — full-width */}
            <FieldWrap label="Street Address" htmlFor="d-street" error={errors.street_address}>
              <input id="d-street" data-testid="dealer-street" maxLength={100} value={form.street_address} onChange={set("street_address")}
                aria-invalid={!!errors.street_address} className={`${inputCls(errors.street_address)} w-full`} />
            </FieldWrap>

            {/* City (wide) + State (dropdown narrow) + ZIP (narrow) */}
            <div className="flex flex-wrap gap-4">
              <FieldWrap label="City" htmlFor="d-city" error={errors.city} className="flex-1 min-w-[200px]">
                <input id="d-city" data-testid="dealer-city" maxLength={60} value={form.city} onChange={set("city")}
                  aria-invalid={!!errors.city} className={`${inputCls(errors.city)} w-full sm:max-w-xs`} />
              </FieldWrap>
              <FieldWrap label="State" htmlFor="d-state" error={errors.state}>
                <select id="d-state" data-testid="dealer-state" value={form.state} onChange={set("state")}
                  aria-invalid={!!errors.state} className={`${inputCls(errors.state)} w-full sm:w-28`}>
                  <option value="">—</option>
                  {US_STATES.map((s) => <option key={s} value={s}>{s}</option>)}
                </select>
              </FieldWrap>
              <FieldWrap label="ZIP" htmlFor="d-zip" error={errors.zip_code}>
                <input id="d-zip" data-testid="dealer-zip" maxLength={10} value={form.zip_code} onChange={set("zip_code")}
                  aria-invalid={!!errors.zip_code} className={`${inputCls(errors.zip_code)} w-full sm:w-32`} />
              </FieldWrap>
            </div>

            <div className="crosshair-divider my-4" />
            <label className="flex items-start gap-3 cursor-pointer" data-testid="dealer-terms-label">
              <input type="checkbox" data-testid="dealer-agree" checked={form.agree_terms} onChange={set("agree_terms")}
                className="accent-olive w-5 h-5 mt-0.5 shrink-0" aria-invalid={!!errors.agree_terms} />
              <span className="text-sm font-body text-secondary-foreground">
                I confirm I am authorized to sell these items and agree to the <span className="text-olive-bright">OpticSwap Dealer Terms</span>.
              </span>
            </label>
            {errors.agree_terms && <p role="alert" data-testid="dealer-terms-error" className="text-xs font-mono text-blaze">{errors.agree_terms}</p>}

            <button data-testid="dealer-submit" disabled={busy}
              className="w-full bg-olive hover:bg-olive-bright disabled:opacity-50 text-white font-heading font-bold uppercase text-lg py-3.5 transition-colors">
              {busy ? "Submitting..." : "Submit Application"}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
